import { EditorContent, useEditor } from '@tiptap/react'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { clipboardTextParser, richTextStyles } from '@shared/messaging/src/RichTextHelper'
import { useMe } from '@shared/providers/src/MeProvider'
import { getTestId } from '@shared/utils'

import { useUserOutOfOfficeListener } from '@hooks'
import { outlineRichTextEditorStyling } from '@utils/StylesHelper'
import { extensions } from '@components/RichText'
import User from '@components/User'

import { useSendMessage } from './MessageSupport.hooks'

MessageSupport.propTypes = {
  /** Whether the modal is shown */
  open: PropTypes.bool.isRequired,

  /** Called after any action or the dialog is closed*/
  onClose: PropTypes.func.isRequired,
}

/**
 * Display a dialog to send a message to customer support.
 * This handles the api POST to send the message.
 */
export default function MessageSupport({ open, onClose, ...rest }) {
  const testId = getTestId(rest, 'message-support-dialog')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ 'data-testid': testId }}
      aria-labelledby={`${testId}-title`}
      aria-describedby={`${testId}-description`}
      role="dialog"
      aria-modal="true"
    >
      <Content onClose={onClose} data-testid={testId} titleId={`${testId}-title`} descriptionId={`${testId}-description`} />
    </Dialog>
  )
}

function Content({ onClose, ...rest }) {
  const me = useMe()
  const support = me.supportUser
  const testId = getTestId(rest)

  const editor = useEditor({
    extensions,
    autofocus: 'end',
    editorProps: { clipboardTextParser },
  })

  const sendMessage = useSendMessage()

  const handleSendMessage = () => {
    const message = editor.getJSON()
    return sendMessage.mutateAsync({ message: JSON.stringify(message) }).then(() => onClose())
  }

  useUserOutOfOfficeListener(support)

  return (
    <>
      <DialogTitle id={`${testId}-title`}>Message Customer Support</DialogTitle>
      <DialogContent id={`${testId}-description`}>
        <Stack spacing={1}>
          <User data={support} />
          <Box
            sx={[outlineRichTextEditorStyling, richTextStyles, { '& .tiptap': { minHeight: 100, maxHeight: 300 } }]}
            aria-label="Message content editor"
            role="textbox"
          >
            <EditorContent editor={editor} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Customer Support business hours are Mon-Fri 8am - 6pm EST. It may take up to 1-2 days to respond to your message if it was sent
            during regular business hours.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" data-testid={`${testId}-cancel`} aria-label="Cancel sending message">
          Cancel
        </Button>
        <Button
          loading={sendMessage.isPending}
          onClick={handleSendMessage}
          variant="contained"
          data-testid={`${testId}-send`}
          disabled={editor.isEmpty}
          aria-label="Send message to customer support"
        >
          Send
        </Button>
      </DialogActions>
    </>
  )
}
